import { useNavigate } from 'react-router';
import { Badge, Card, Skeleton } from '@monksflow/monks-ui';
import { useQuery } from '@tanstack/react-query';
import { projectsListQuery, type PendingAssetPreview } from '@/entities/project';
import { AssetThumbnail } from '@/entities/project-file';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { ROUTES } from '@/shared/config/routes';
import { useGetDownloadUrl } from '@/shared/hooks/useGetDownloadUrl/useGetDownloadUrl';
import { useTranslation } from 'react-i18next';

interface FolderItemData {
  project: {
    platformProjectId: string;
    title: string | null | undefined;
  };
  folder: {
    id: string;
    name: string;
    pendingAsset: PendingAssetPreview;
    pendingCount: number;
  };
}

const RowSkeleton = () => (
  <div
    data-testid="skeleton-loader"
    className="flex h-17.5 items-center gap-sm rounded-lg border border-border-light bg-transparent p-sm"
  >
    <Skeleton className="size-10 shrink-0" />
    <div className="flex flex-1 flex-col gap-2xs">
      <Skeleton className="h-3.5 w-1/2" />
      <Skeleton className="h-3 w-1/3" />
    </div>
    <Skeleton className="h-6 w-28 rounded-full" />
  </div>
);

const FolderAssetThumbnail = ({ asset }: { asset: PendingAssetPreview }) => {
  const { downloadUrl } = useGetDownloadUrl(asset.assetId);

  return (
    <div className="size-10 shrink-0 overflow-hidden rounded-sm bg-muted">
      <AssetThumbnail
        file={{
          fileName: asset.fileName,
          fileType: asset.fileType,
          assetType: asset.assetType,
          downloadUrl,
        }}
        className="size-full"
        iconSize="size-4"
      />
    </div>
  );
};

const FolderItem = ({ folder }: { folder: FolderItemData }) => {
  const { t } = useTranslation('springboard');

  const navigate = useNavigate();

  return (
    <Card
      onClick={() =>
        void navigate(
          ROUTES.PROJECT_FOLDER_DETAIL(folder.project.platformProjectId, folder.folder.id),
        )
      }
      className="flex-row cursor-pointer items-center gap-sm gap-y-0 rounded-lg border border-border-light bg-transparent p-sm shadow-none transition-[border-color] duration-fast ease-out hover:border-border-strong"
    >
      <FolderAssetThumbnail asset={folder.folder.pendingAsset} />

      <div className="flex min-w-0 flex-1 flex-col gap-3xs">
        <span className="truncate text-sm leading-5 font-medium text-foreground">
          {folder.folder.name}
        </span>
        <span className="truncate text-xs leading-4 text-muted-foreground">
          {t('dashboard.assetReviews.projectTag', {
            projectTitle: folder.project.title ?? t('dashboard.assetReviews.untitledProject'),
          })}
        </span>
      </div>
      <Badge variant="warning" roundness="full" size="sm" className="shrink-0">
        {t('dashboard.assetReviews.assetCount', {
          count: folder.folder.pendingCount,
        })}
      </Badge>
    </Card>
  );
};

export const AssetReviews = () => {
  const { t } = useTranslation('springboard');

  const workspaceId = useCurrentWorkspaceId();

  const { data, isPending } = useQuery({
    ...projectsListQuery(workspaceId, {
      page: 1,
      pageSize: 8,
      pendingOnly: true,
    }),
    // Pending counts change while the user is off reviewing assets, so the
    // widget must reflect the live state every time Home remounts rather than
    // serving the cached list until the global staleTime elapses.
    refetchOnMount: 'always',
  });

  const folders: FolderItemData[] = [];

  data?.items.forEach((project) => {
    // No fallback to the internal `id`: the folder route is project-scoped, and
    // since both are UUIDs the wrong one reaches the guard as an unknown scope
    // and answers 403 rather than failing validation.
    const { platformProjectId } = project;
    if (!platformProjectId) return;

    project.activity.folders?.forEach((folder) => {
      folders.push({
        project: {
          platformProjectId,
          title: project.title,
        },
        folder: {
          id: folder.folderId,
          name: folder.folderName,
          pendingAsset: folder.pendingAsset,
          pendingCount: folder.pendingCount,
        },
      });
    });
  });

  if (!isPending && folders.length === 0) {
    return null;
  }

  const visibleFolders = folders.slice(0, 8);

  return (
    <section className="content-block gap-md" data-testid="home-asset-reviews">
      <div className="flex items-end gap-xs">
        <h2 className="type-heading-sm leading-5 tracking-[-0.02em]">
          {t('dashboard.assetReviews.assetsAwaitingApproval')}
        </h2>
        <span className="text-sm text-foreground-alt">&bull;</span>
        <span className="text-sm text-foreground-alt">
          {t('dashboard.assetReviews.displayedPerFolder')}
        </span>
      </div>

      <div className="grid w-full grid-cols-1 gap-grid md:grid-cols-2 lg:grid-cols-4">
        {isPending
          ? Array.from({ length: 8 }).map((_, i) => <RowSkeleton key={i} />)
          : visibleFolders.map((folder) => <FolderItem key={folder.folder.id} folder={folder} />)}
      </div>
    </section>
  );
};
